import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import OrderDetail from '../../components/orders/OrderDetail';
import Alert from '../../components/common/Alert';
import Loading from '../../components/common/Loading';
import { getOrder, getCustomerInvoiceLinks } from '../../services/orders';
import { PAYMENT_STATUS_LABELS } from '../../utils/constants';
import { formatDate } from '../../utils/formatters';
import './OrderDetail.css';

const OrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoiceLinks, setInvoiceLinks] = useState(null);
  const [fetchingLinks, setFetchingLinks] = useState(false);
  const [linkError, setLinkError] = useState(false);

  const successMessage = location.state?.successMessage;

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        setError(null);
        const orderData = await getOrder(parseInt(id, 10));
        setOrder(orderData);
      } catch (err) {
        console.error('Failed to fetch order:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

  if (loading) {
    return (
      <div className="order-detail-page order-detail-page--loading">
        <Loading />
        <p>Loading order…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-detail-page order-detail-page--error">
        <Alert type="error">
          {error.message || 'Failed to load order. Please try again.'}
        </Alert>
        <button className="order-detail-page__back" onClick={() => navigate('/orders')}>
          ← Back to Orders
        </button>
      </div>
    );
  }

  const handleGetLinks = async () => {
    setFetchingLinks(true);
    setLinkError(false);
    try {
      const data = await getCustomerInvoiceLinks(order.id);
      setInvoiceLinks(data);
    } catch {
      setLinkError(true);
    } finally {
      setFetchingLinks(false);
    }
  };

  return (
    <div className="order-detail-page">
      <button className="order-detail-page__back" onClick={() => navigate('/orders')}>
        ← All Orders
      </button>

      {successMessage && (
        <div className="order-detail-page__alert">
          <Alert type="success">{successMessage}</Alert>
        </div>
      )}

      <OrderDetail order={order} isEmployee={false} />

      {order.stripe_invoice_id && (
        <div className="order-detail-page__invoice">
          <h2 className="order-detail-page__invoice-title">Invoice</h2>
          <div className="order-detail-page__invoice-row">
            {order.payment_status && (
              <span className={`order-detail-page__invoice-badge order-detail-page__invoice-badge--${order.payment_status}`}>
                {PAYMENT_STATUS_LABELS[order.payment_status] || order.payment_status}
              </span>
            )}
            {order.payment_date && (
              <span className="order-detail-page__invoice-date">
                Paid {formatDate(order.payment_date)}
              </span>
            )}
            {invoiceLinks ? (
              <>
                {invoiceLinks.hosted_invoice_url && (
                  <a href={invoiceLinks.hosted_invoice_url} target="_blank" rel="noreferrer" className="order-detail-page__invoice-link">
                    View Invoice ↗
                  </a>
                )}
                {invoiceLinks.invoice_pdf && (
                  <a href={invoiceLinks.invoice_pdf} target="_blank" rel="noreferrer" className="order-detail-page__invoice-link">
                    Download PDF ↗
                  </a>
                )}
              </>
            ) : (
              <>
                <button
                  className="order-detail-page__invoice-link-btn"
                  disabled={fetchingLinks}
                  onClick={handleGetLinks}
                >
                  {fetchingLinks ? 'Loading…' : 'Get Invoice Links'}
                </button>
                {linkError && (
                  <span className="order-detail-page__invoice-link-error">Failed — try again</span>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetailPage;
